import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InspirationDetailLayout } from "web_myplann/Inspiration/Components/InspirationDetailLayout";
import { RetirementAddLoanSheet } from "web_myplann/Inspiration/Retirement/Components/RetirementAddLoanSheet";
import { RetirementColdIntro } from "web_myplann/Inspiration/Retirement/Components/RetirementColdIntro";
import { RetirementExpensesSheet } from "web_myplann/Inspiration/Retirement/Components/RetirementExpensesSheet";
import { RetirementResult } from "web_myplann/Inspiration/Retirement/Components/RetirementResult";
import { RetirementSkeleton } from "web_myplann/Inspiration/Retirement/Components/RetirementSkeleton";
import { RetirementStatePensionSheet } from "web_myplann/Inspiration/Retirement/Components/RetirementStatePensionSheet";
import { RETIREMENT_IMAGE } from "web_myplann/Inspiration/Retirement/constants";
import { useRetirementCtas } from "web_myplann/Inspiration/Retirement/Hooks/useRetirementCtas";
import { useRetirementJourney } from "web_myplann/Inspiration/Retirement/Hooks/useRetirementJourney";

const PageResources = Resources.Inspiration.Retirement;

export const RetirementInspirationPage: FC = () => {
	const { t } = useResource();
	const navigate = useNavigate();
	const {
		context,
		isLoaded,
		activeSheet,
		openSheet,
		closeSheet,
		patchInputs,
		submitInputs,
		toggleDrop,
		addManualDrop,
		removeManualDrop,
		saveExpenses,
	} = useRetirementJourney();
	const { goalRecap, primaryCta, secondaryCta } = useRetirementCtas({
		context,
		onStart: () => openSheet("expenses"),
	});

	const isCold = context.level === "cold";

	const openBalance = () => {
		closeSheet();
		navigate(Routing.Finances.Intro.path());
	};

	return (
		<>
			<InspirationDetailLayout
				scrollResetKey={isCold}
				image={RETIREMENT_IMAGE}
				category={t(PageResources.category)}
				title={t(PageResources.title)}
				subtitle={t(PageResources.subtitle)}
				goalRecap={goalRecap}
				primaryCta={primaryCta}
				secondaryCta={secondaryCta}
				isLoading={!isLoaded}
			>
				{!isLoaded && <RetirementSkeleton />}
				{isLoaded && isCold && <RetirementColdIntro />}
				{isLoaded && !isCold && (
					<RetirementResult
						context={context}
						patchInputs={patchInputs}
						openSheet={openSheet}
						toggleDrop={toggleDrop}
						removeManualDrop={removeManualDrop}
					/>
				)}
			</InspirationDetailLayout>

			<RetirementExpensesSheet
				open={activeSheet === "expenses"}
				onClose={closeSheet}
				onSubmit={saveExpenses}
				onOpenBalance={openBalance}
			/>

			<RetirementAddLoanSheet
				open={activeSheet === "addLoan"}
				retirementYear={context.retirementYear}
				onClose={closeSheet}
				onSubmit={addManualDrop}
			/>

			<RetirementStatePensionSheet
				open={activeSheet === "statePension"}
				initial={context.statePension}
				onClose={closeSheet}
				onSubmit={(statePension) => submitInputs({ statePension })}
			/>
		</>
	);
};
